import { useCallback, useEffect, useRef, useState } from "react";
import type { ScenarioDetail } from "@shared/schemas/scenario";
import { useOnlineStatus } from "@client/hooks/useOnlineStatus";
import { useSaveScenarioModel } from "@client/hooks/useScenarios";
import { clearDraftMirror, writeDraftMirror } from "@client/lib/draftMirror";
import { conflictFrom } from "@client/lib/scenariosApi";
import { reconcileIds, type DraftAction, type DraftState } from "@client/lib/scenarioDraft";

/**
 * The save pipeline: autosave, `Ctrl+S`, the recovery mirror, the unload guard
 * and the conflict hand-off. Everything in docs/08-frontend.md#save-autosave-and-conflicts
 * that is not a dialog.
 *
 * It lives in a hook rather than in `ScenarioEditorPage` because it is a state
 * machine with four timers-and-listeners' worth of cleanup, and because the page
 * is long enough already. The page keeps the decisions a person makes — which
 * copy wins a conflict — and this keeps the mechanics.
 */

/** Long enough to cover a sentence being typed, short enough to feel automatic. */
export const AUTOSAVE_DELAY_MS = 2500;

export type SaveStatus = "clean" | "dirty" | "saving" | "error";

export interface ConflictState {
  currentVersion: number;
  current: ScenarioDetail;
}

export interface ScenarioSave {
  status: SaveStatus;
  /**
   * The browser reports no network. Separate from `status` because it is a
   * condition rather than a step: a draft can be dirty *and* offline, and the
   * sentence a person needs then is about the connection, not about the save.
   */
  offline: boolean;
  lastSavedAt: Date | null;
  error: unknown;
  conflict: ConflictState | null;
  /** Save immediately — the Save button and `Ctrl+S`. */
  saveNow: () => void;
  /** Re-save the local draft against the version the server now holds. */
  keepMine: () => void;
  /** Drop the conflict without saving — after "take theirs", or a plain cancel. */
  clearConflict: () => void;
}

interface Options {
  scenarioId: string;
  draft: DraftState;
  dispatch: (action: DraftAction) => void;
  /** False for a read-only viewer: nothing is scheduled, stored or guarded. */
  enabled: boolean;
}

export function useScenarioSave({ scenarioId, draft, dispatch, enabled }: Options): ScenarioSave {
  const save = useSaveScenarioModel(scenarioId);
  const online = useOnlineStatus();
  // `save` is a fresh object every render; `mutateAsync` is not. Depending on
  // the object would give `run` a new identity on every render, and the autosave
  // effect below would restart its clock each time — including on renders the
  // user did not cause, which is how an autosave never fires.
  const { mutateAsync } = save;
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [conflict, setConflict] = useState<ConflictState | null>(null);

  // The draft is read inside callbacks that outlive the render they were made
  // in — a timer, a keydown listener — so they read it through a ref rather than
  // capturing whichever draft existed when they were created.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const inFlight = useRef(false);

  const run = useCallback(
    async (version: number) => {
      if (inFlight.current) return;
      const sent = draftRef.current.model;
      inFlight.current = true;
      setError(null);
      try {
        const response = await mutateAsync({ version, model: sent });
        const stored = response.scenario;
        // Unchanged since the request left: take the server's model whole, which
        // is how trimmed names and server-assigned ids reach the editor.
        if (draftRef.current.model === sent) {
          dispatch({ type: "loaded", model: stored.model, version: stored.version });
          clearDraftMirror(scenarioId);
        } else {
          // Someone kept typing. Keep their work, take only the new ids, and
          // stay dirty so the next autosave carries the rest.
          dispatch({
            type: "restored",
            model: reconcileIds(draftRef.current.model, sent, stored.model),
            version: stored.version,
          });
        }
        setLastSavedAt(new Date());
      } catch (cause) {
        const clash = conflictFrom(cause);
        if (clash) setConflict(clash);
        else setError(cause);
      } finally {
        inFlight.current = false;
      }
    },
    [dispatch, mutateAsync, scenarioId],
  );

  const saveNow = useCallback(() => {
    if (!enabled || conflict) return;
    // Offline, the request is a guaranteed "Failed to fetch" a second from now.
    // Saying so up front beats replacing the status with an error the person can
    // do nothing about — the mirror below already has their work, and the
    // reconnect effect will send it.
    if (!online) return;
    void run(draftRef.current.version);
  }, [conflict, enabled, online, run]);

  const keepMine = useCallback(() => {
    if (!conflict) return;
    const version = conflict.currentVersion;
    setConflict(null);
    // Against *their* version, which is what "keep mine" means: my model wins,
    // their save is still in the history, and the next person sees one more version.
    void run(version);
  }, [conflict, run]);

  /* ----------------------------------------------------------- the autosave */

  useEffect(() => {
    if (!enabled || !draft.dirty || conflict !== null) return;
    // Offline, an autosave every 2.5 seconds is 24 failed requests a minute and
    // a status that flickers between "unsaved" and "save failed". `online` is a
    // dependency, so the first keystroke after the connection returns starts the
    // clock again — and the effect below does not even wait for one.
    if (!online) return;
    // Trailing edge only. A leading-edge save would fire on the first pixel of a
    // slider drag and then queue a second one for where the thumb landed.
    const handle = window.setTimeout(() => void run(draftRef.current.version), AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(handle);
    // `draft.model` is the dependency that matters: a new object per edit is
    // what restarts the clock.
  }, [draft.model, draft.dirty, conflict, enabled, online, run]);

  /* ------------------------------------------------------------- reconnect */

  // Coming back online with unsaved work, save it without waiting for another
  // edit. Someone who closed a laptop lid mid-sentence and opened it on the
  // train should find the scenario saved, not "unsaved changes" from an hour ago.
  const wasOnline = useRef(online);
  useEffect(() => {
    const reconnected = online && !wasOnline.current;
    wasOnline.current = online;
    if (!reconnected || !enabled || conflict !== null) return;
    if (!draftRef.current.dirty) return;
    void run(draftRef.current.version);
  }, [conflict, enabled, online, run]);

  /* -------------------------------------------------------------- the mirror */

  useEffect(() => {
    if (!enabled) return;
    if (!draft.dirty) return;
    writeDraftMirror(scenarioId, {
      version: draft.version,
      savedAt: new Date().toISOString(),
      model: draft.model,
    });
  }, [draft.model, draft.dirty, draft.version, enabled, scenarioId]);

  /* --------------------------------------------------------------- keyboard */

  useEffect(() => {
    if (!enabled) return;
    function onKeyDown(event: KeyboardEvent) {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "s") return;
      // The browser's "save page" dialog over an editor is never what was meant.
      event.preventDefault();
      saveNow();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, saveNow]);

  /* ----------------------------------------------------------------- unload */

  useEffect(() => {
    if (!enabled || (!draft.dirty && !inFlight.current)) return;
    function onBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      // Browsers show their own wording and ignore ours, but `returnValue` is
      // still what older ones check to decide whether to ask at all.
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [draft.dirty, enabled]);

  const status: SaveStatus = save.isPending
    ? "saving"
    : error !== null
      ? "error"
      : draft.dirty
        ? "dirty"
        : "clean";

  return {
    status,
    offline: !online,
    lastSavedAt,
    error,
    conflict,
    saveNow,
    keepMine,
    clearConflict: () => setConflict(null),
  };
}
